<script setup>
/**
 * GiftRoomSteps: the gift room, redesigned for a complete novice (2026-10-29,
 * Chris approved round 3: Claude outputs/gift-room-2026-10-29/DESIGN.md and
 * prototype.html; final shots 22 to 26).
 *
 * One thing per step. The gift stays in view the whole time (the left page on
 * a computer, a slim strip of cards on a phone), the step is the right page,
 * seven quiet dots at the top say where you are, one gold action per step,
 * and the ✕ top right on every step closes the room (the page's exitReading).
 *
 * The page (ReaderPage) owns the mechanics this draws: the draw (its picker is
 * teleported into step 2's slot, `draw-slot`), their card's reading (streamed
 * into `myCard`), the write-back (GiftReply), the circle ask, the membership
 * door before the stars (`stars`), their own reading (`own`), the Mirror.
 * This component owns only step 5's date and step 6's star (the birth moment
 * and You & <sender> endpoints).
 *
 * Chris's overrides (2026-10-29): no "On us" pill and no new free-reading rule
 * (the gold button carries the normal reading price when prices show); no
 * sender birthday guess; no "Keep this… portrait" line; no "Your sign and the
 * two of you are kept here."
 */
import { computed, nextTick, ref, watch, onMounted } from 'vue'
import GiftReply from '@/components/ui/GiftReply.vue'
import CircleAsk from '@/components/ui/CircleAsk.vue'
import KeepThis from '@/components/ui/KeepThis.vue'
import GiftStar from '@/components/ui/GiftStar.vue'
import BirthMoment from '@/components/widgets/BirthMoment.vue'
import { getBirthMoment, saveBirthMoment, ourStars } from '@/api-service'
import { initialOf } from '@/utils/coven-ring'
import { reducedMotionNow } from '@/composables/useReducedMotion'
import { momentSegments } from '@/utils/introduction'
import {
  STEP_COUNT, DRAW_LINE, cardAsk, starsLead, BIRTHDAY_ASK, signWords, signGlyph, theirSignLine, OWN_HERE,
  ownAction, capFirst, noSenderBirthday,
} from '@/utils/gift-steps'
import '@/assets/fonts/letter/letter-fonts.css'
import CaptionBeats from '@/components/ui/share/CaptionBeats.vue'

const props = defineProps({
  step: { type: Number, default: 1 },
  // GiftLetter's props (care-room.roomLetter): image, note, fromName, title,
  // readerName, readerAvatar, cards [{ name, reversed, image_url, position, phrase, text }].
  letter: { type: Object, default: () => ({}) },
  // The sender as the recipient says it ("Chris Lee", "your son").
  sender: { type: String, default: '' },
  forName: { type: String, default: '' },
  slotLabel: { type: String, default: '' },
  mystic: { type: Object, default: () => ({}) },
  firstMessage: { type: String, default: '' },
  heldQuestion: { type: String, default: '' },
  // Step 1's gold action: { key, label } (gift_deeper, or gift_signin signed out on a claimed package).
  firstAction: { type: Object, default: () => ({ key: 'gift_deeper', label: 'Draw my card' }) },
  // Their card, in the talk-card shape: { card, image, reversed, question, line, reading }.
  myCard: { type: Object, default: null },
  // GiftReply's props for the write-back: { gift, room, short, inv, beforeSend }.
  reply: { type: Object, default: () => ({}) },
  // Written back: { rating, note }.
  replied: { type: Object, default: null },
  thanksLine: { type: String, default: '' },
  circleAsk: { type: Object, default: null },
  inv: { type: String, default: '' },
  // You & <sender> was made already (the Mirror link on step 7).
  stars: { type: Boolean, default: false },
  priceLabel: { type: String, default: '' },
  // Room lines that are not a step's own ("kept", the introduction's end): a
  // string, or { text, link } where link is the words that open the Quest (R8.3).
  notes: { type: Array, default: () => [] },
  keepOffer: { type: Boolean, default: false },
  // The beat between the sign and the star (DESIGN: about 2.6 s; 1.2 s reduced).
  beatMs: { type: Number, default: null },
})
const emit = defineEmits(['close', 'chip', 'step', 'draw-slot', 'thank-sent', 'stars', 'stars-ready', 'own', 'mirror',
  'dated', 'open-quest'])
const noteText = (n) => (typeof n === 'string' ? n : (n && n.text) || '')
const noteLink = (n) => (n && typeof n === 'object' && n.link) || ''

const reduced = reducedMotionNow()
const sender = computed(() => String(props.sender || '').trim() || 'them')
const Sender = computed(() => capFirst(sender.value))
const cards = computed(() => (props.letter && Array.isArray(props.letter.cards)) ? props.letter.cards : [])
const fromEyebrow = computed(() => props.letter && props.letter.fromName
  ? `${props.letter.fromName} was thinking of you` : 'Someone was thinking of you')
const stripFrom = computed(() => props.letter && props.letter.fromName ? `From ${props.letter.fromName}` : '')
// The picture's line (picture-caption 2026-10-30): after the note, before the
// title, once the picture has loaded (never under an empty frame).
const picLoaded = ref(false)
// A picture that does not load leaves no empty frame (e2e walk 2026-10-31):
// it goes, and the paper opens on the note, as the old room (GiftLetter) did.
const picFailed = ref(false)
watch(() => props.letter && props.letter.image, () => { picLoaded.value = false; picFailed.value = false })
const showPic = computed(() => !!(props.letter && props.letter.image) && !picFailed.value)
const showCap = computed(() => showPic.value && !!props.letter.caption && picLoaded.value)
// A card picture that fails: the card's blank frame, never a broken image.
const failedArt = ref({})
const artFailed = (where, i) => !!failedArt.value[where + i]
function artBroke (where, i) { failedArt.value = { ...failedArt.value, [where + i]: true } }
watch(cards, () => { failedArt.value = {} })
// An avatar that does not load (the reader's, the Mystic's): the name's
// initial in its place, as the app's other avatars do (MysticMark), never a
// broken image. A new avatar gets its chance again.
const readerAvFailed = ref(false)
watch(() => props.letter && props.letter.readerAvatar, () => { readerAvFailed.value = false })
const mysticAvFailed = ref(false)
watch(() => props.mystic && props.mystic.avatar, () => { mysticAvFailed.value = false })

// ── Step 2: the deck's place (the page teleports its picker here) ─────────
// A fresh id each time the slot is drawn: Vue resolves a Teleport's target
// when its `to` changes, so a new id is a new target (never a stale node).
const slotEl = ref(null)
const slotId = `gr-draw-slot-${Math.random().toString(36).slice(2, 8)}`
watch(slotEl, (el) => emit('draw-slot', el ? slotId : ''), { flush: 'post' })

// ── Step 3 ────────────────────────────────────────────────────────────────
const cardTitle = computed(() => {
  const c = props.myCard
  if (!c || !c.card) return ''
  return c.line ? `${c.card}: ${c.line}` : c.card
})
const sealed = computed(() => props.replied ? { rating: props.replied.rating || 0, note: props.replied.note || '' } : null)
function onReplySent(p) { emit('thank-sent', p) }

// ── Step 5: the birthday, the date only ───────────────────────────────────
const phase = ref('loading')         // loading | ask | sign
const sun = ref('')
const saving = ref(false)
const errors = ref(null)
const starsResult = ref(null)
const starsError = ref(false)
const theirSun = computed(() => {
  const r = starsResult.value
  return (r && r.reading && r.reading.their_signs && r.reading.their_signs.sun) || ''
})
const mySun = computed(() => {
  const r = starsResult.value
  return sun.value || (r && r.reading && r.reading.my_signs && r.reading.my_signs.sun)
    || (r && r.my_signs && r.my_signs.sun) || ''
})

async function startBirthday() {
  phase.value = 'loading'
  let r = null
  try { r = await getBirthMoment() } catch (e) { r = null }
  if (r && r.moment && r.moment.birthdate) {
    sun.value = (r.signs && (r.signs.sun || r.signs.sun_text)) || ''
    afterDate()
    return
  }
  phase.value = 'ask'
}

async function saveDate(out) {
  saving.value = true; errors.value = null
  try {
    const r = await saveBirthMoment({ birthdate: out.birthdate, time: null })
    sun.value = (r && r.signs && (r.signs.sun || r.signs.sun_text)) || ''
    emit('dated')
    afterDate()
  } catch (e) {
    errors.value = (e && e.response && e.response.data && e.response.data.errors) || { _: 'That did not save. Try again.' }
  } finally { saving.value = false }
}

async function afterDate() {
  phase.value = 'sign'
  await loadStars()
  // The sender has no birthday yet (gift room walk #8): no star to beat on
  // to. Their sign and one line stay, and Continue skips to step 7.
  if (waiting.value) return
  const beat = props.beatMs !== null ? props.beatMs : (reduced ? 1200 : 2600)
  setTimeout(() => {
    if (props.step === 5) emit('step', 6)
  }, beat)
}

async function loadStars() {
  starsError.value = false
  try {
    const r = await ourStars(props.inv)
    starsResult.value = r
    if (r && (r.status === 'ready' || r.status === 'waiting_for_sender')) emit('stars-ready', r)
    else starsError.value = true
  } catch (e) { starsError.value = true }
}

// ── Step 6: the star ──────────────────────────────────────────────────────
const reading = computed(() => (starsResult.value && starsResult.value.status === 'ready' && starsResult.value.reading) || null)
const waiting = computed(() => !!(starsResult.value && starsResult.value.status === 'waiting_for_sender'))
const starSize = ref(typeof window !== 'undefined' && window.innerWidth < 900 ? 230 : 290)
const pairLine = computed(() => {
  const me = String(props.forName || '').trim()
  const a = me ? `${me}${mySun.value ? ' ' + signGlyph(mySun.value) : ''}` : ''
  const b = `${Sender.value}${theirSun.value ? ' ' + signGlyph(theirSun.value) : ''}`
  return a ? `${a} · ${b}` : b
})

// ── Step 7 ────────────────────────────────────────────────────────────────
const forSomeone = ref(false)
const own = computed(() => ownAction(forSomeone.value))

// Each step starts at its top; step 5 and 6 load what they show.
const stageEl = ref(null)
function arrive(step) {
  if (step === 5) startBirthday()
  if (step === 6 && !starsResult.value) loadStars()
  nextTick(() => {
    const el = stageEl.value
    try { if (el && el.closest) { const s = el.closest('.grs'); if (s) s.scrollTop = 0 } } catch (e) { /* */ }
  })
}
watch(() => props.step, arrive)
onMounted(() => arrive(props.step))

const goTo = (n) => emit('step', n)
</script>

<template>
  <section class="grs" :class="'grs-step-' + step" data-testid="gift-room-steps" :data-step="step">
    <div class="gr-top">
      <div class="gr-dots" role="img" :aria-label="`Step ${step} of ${STEP_COUNT}`" data-testid="gr-dots">
        <i v-for="n in STEP_COUNT" :key="n" :class="{ done: n < step, on: n === step }" data-testid="gr-dot"></i>
      </div>
      <button type="button" class="gr-x" aria-label="Close" data-testid="gr-close" @click="emit('close')">✕</button>
    </div>

    <div class="gr-page">
      <!-- The gift, kept in view: the paper on a computer, a strip of cards on a phone. -->
      <aside class="gr-gift" :class="{ strip: step > 1 }" data-testid="gr-gift">
        <div class="gr-paper" :class="{ 'no-lead': !showPic }" data-testid="gr-paper">
          <div v-if="showPic" class="gr-pic" data-testid="gr-pic"><img :src="letter.image" :alt="letter.caption || ''"
            @load="picLoaded = true" @error="picFailed = true" /></div>
          <div class="gr-note" data-testid="gr-note">
            <span class="gr-seal" aria-hidden="true">✦</span>
            <div class="gr-ey">{{ fromEyebrow }}</div>
            <p v-if="letter.note">{{ letter.note }}</p>
          </div>
          <p v-if="showCap" class="gr-cap" data-testid="gr-caption"><CaptionBeats :text="letter.caption" /></p>
          <h2 v-if="letter.title" class="gr-title">{{ letter.title }}</h2>
          <div v-if="letter.readerName" class="gr-by">
            <img v-if="letter.readerAvatar && !readerAvFailed" :src="letter.readerAvatar" alt="" @error="readerAvFailed = true" />
            <span v-else-if="letter.readerAvatar" class="gr-av-ph" data-testid="gr-av-ph" aria-hidden="true">{{ initialOf(letter.readerName) }}</span>
            <span>read by <b>{{ letter.readerName }}</b></span>
          </div>
          <div v-if="stripFrom" class="gr-from">{{ stripFrom }}</div>
          <div class="gr-row">
            <div v-for="(c, i) in cards" :key="i" class="gr-mc" data-testid="gr-gift-card">
              <div class="f"><img v-if="c.image_url && !artFailed('gift', i)" :class="{ rev: c.reversed }" :src="c.image_url" :alt="c.name"
                @error="artBroke('gift', i)" /></div>
              <span lang="en">{{ c.position }}</span>
            </div>
            <span class="gr-plus" aria-hidden="true">✦</span>
            <div class="gr-mc yours" :class="{ filled: !!(myCard && myCard.image) }" data-testid="gr-gift-yours">
              <div class="f">
                <img v-if="myCard && myCard.image" :class="{ rev: myCard.reversed }" :src="myCard.image" :alt="myCard.card" />
                <template v-else>✦</template>
              </div>
              <span>{{ slotLabel || forName || 'Yours' }}</span>
            </div>
          </div>
        </div>
      </aside>

      <main ref="stageEl" class="gr-stage" :key="step">
        <template v-for="(n, i) in notes" :key="'n' + i">
          <!-- The end of her introduction (R8.3): its link opens the Quest sheet (R8.5). -->
          <div v-if="noteLink(n)" class="gr-say gr-note-line" data-testid="intro-line"><template
            v-for="(seg, si) in momentSegments(noteText(n), noteLink(n))" :key="si"><button v-if="seg.link" type="button"
            class="gr-note-link" data-testid="intro-line-link" @click="emit('open-quest')">{{ seg.text }}</button><template
            v-else>{{ seg.text }}</template></template></div>
          <div v-else class="gr-say gr-note-line">{{ noteText(n) }}</div>
        </template>

        <!-- 1 · Arrival -->
        <template v-if="step === 1">
          <div class="gr-say">
            <div class="gr-who"><img v-if="mystic.avatar && !mysticAvFailed" :src="mystic.avatar" alt="" @error="mysticAvFailed = true" /><span
              v-else-if="mystic.avatar" class="gr-av-ph" data-testid="gr-av-ph" aria-hidden="true">{{ initialOf(mystic.name) }}</span>{{ mystic.name }}</div>
            {{ firstMessage }}
          </div>
          <div class="gr-letter">
            <div v-for="(c, i) in cards" :key="i" class="gr-lc" data-testid="gr-letter-card">
              <div class="f"><img v-if="c.image_url && !artFailed('letter', i)" :class="{ rev: c.reversed }" :src="c.image_url" alt=""
                @error="artBroke('letter', i)" /></div>
              <div>
                <div class="k">{{ c.position }}<i>· {{ c.name }}<template v-if="c.reversed">, reversed</template></i></div>
                <b v-if="c.phrase">{{ c.phrase }}</b>
                <p v-if="c.text">{{ c.text }}</p>
              </div>
            </div>
          </div>
          <div class="gr-center gr-mt">
            <button type="button" class="gr-go" data-testid="gr-draw" @click="emit('chip', firstAction.key)">✦ {{ firstAction.label }}</button>
          </div>
        </template>

        <!-- 2 · Draw your card -->
        <template v-else-if="step === 2">
          <div class="gr-say">
            <div class="gr-who"><img v-if="mystic.avatar && !mysticAvFailed" :src="mystic.avatar" alt="" @error="mysticAvFailed = true" /><span
              v-else-if="mystic.avatar" class="gr-av-ph" data-testid="gr-av-ph" aria-hidden="true">{{ initialOf(mystic.name) }}</span>{{ mystic.name }}</div>
            {{ DRAW_LINE }}
          </div>
          <div :id="slotId" ref="slotEl" class="gr-draw" data-testid="gr-draw-slot"></div>
        </template>

        <!-- 3 · Your card -->
        <template v-else-if="step === 3">
          <div v-if="myCard" class="gr-yc" data-testid="gr-your-card">
            <div class="f"><img v-if="myCard.image" :class="{ rev: myCard.reversed }" :src="myCard.image" :alt="myCard.card" /></div>
            <div>
              <div class="gr-ey">Your card · beside {{ letter.fromName || Sender }}’s</div>
              <h3>{{ cardTitle }}</h3>
              <div v-if="myCard.question" class="q">“{{ myCard.question }}”</div>
              <p>{{ myCard.reading }}</p>
            </div>
          </div>
          <div class="gr-say gr-mt">{{ cardAsk(sender) }}</div>
          <div class="gr-acts" data-testid="gr-card-acts">
            <button type="button" class="gr-go" data-testid="gr-thank" @click="goTo(4)">♥ Tell {{ sender }} what it meant</button>
            <button type="button" class="gr-quiet" data-testid="gr-own-from-card" @click="goTo(7)">✎ Ask my own question</button>
          </div>
        </template>

        <!-- 4 · Thank the sender -->
        <template v-else-if="step === 4">
          <template v-if="!sealed">
            <GiftReply inline :gift="reply.gift || {}" :room="reply.room || {}" :short="reply.short || ''"
                       :inv="reply.inv || inv" :before-send="reply.beforeSend || null" :on-sent="onReplySent"
                       @dismiss="emit('stars')" />
          </template>
          <template v-else>
            <GiftReply inline :gift="reply.gift || {}" :room="reply.room || {}" :sealed="sealed" class="gr-receipt" />
            <div v-if="thanksLine" class="gr-say gr-mt">{{ thanksLine }}</div>
            <CircleAsk v-if="circleAsk" :inv="circleAsk.inv || inv" :ask="circleAsk" />
            <div class="gr-say gr-mt">{{ starsLead(sender) }}</div>
            <div class="gr-center">
              <button type="button" class="gr-go" data-testid="gr-stars" @click="emit('stars')">✦ See how our stars meet</button>
            </div>
            <KeepThis v-if="keepOffer" />
          </template>
        </template>

        <!-- 5 · The birthday -->
        <template v-else-if="step === 5">
          <template v-if="phase !== 'sign'">
            <div class="gr-say">
              <div class="gr-who"><img v-if="mystic.avatar && !mysticAvFailed" :src="mystic.avatar" alt="" @error="mysticAvFailed = true" /><span
              v-else-if="mystic.avatar" class="gr-av-ph" data-testid="gr-av-ph" aria-hidden="true">{{ initialOf(mystic.name) }}</span>{{ mystic.name }}</div>
              {{ BIRTHDAY_ASK }}
            </div>
            <div v-if="phase === 'ask'" class="gr-bd">
              <BirthMoment mode="plain" :fields="['date']" :explain="false" :closable="false" submit-label="✦ Show me"
                           :saving="saving" :server-errors="errors" @save="saveDate" />
            </div>
            <!-- A way past it (funnel gap 2, 2026-10-30): quiet, under the date.
                 Step 7 is remembered, so a reload never lands back here. -->
            <div v-if="phase === 'ask'" class="gr-center gr-mt">
              <button type="button" class="gr-link" data-testid="gr-birthday-not-now" @click="goTo(7)">Not now</button>
            </div>
            <p v-else class="gr-dim gr-center">One moment…</p>
          </template>
          <template v-else>
            <!-- Their own sign first, always (from the date, else from the stars' answer). -->
            <div class="gr-sign" data-testid="gr-sign">
              <div v-if="mySun" class="gr-glyph" aria-hidden="true">{{ signGlyph(mySun) }}</div>
              <h3 v-if="mySun">{{ mySun }}</h3>
              <p>{{ signWords(mySun) }}</p>
            </div>
            <!-- The sender has no birthday yet (gift room walk #8): said in one
                 line, and Continue goes on to step 7 without the star. -->
            <template v-if="waiting">
              <div class="gr-say gr-mt" data-testid="gr-no-sender-birthday">{{ noSenderBirthday(sender) }}</div>
              <div class="gr-center gr-mt">
                <button type="button" class="gr-go" data-testid="gr-skip-star" @click="goTo(7)">Continue to your own question ›</button>
              </div>
            </template>
            <template v-else>
              <div class="gr-say gr-mt">{{ theirSignLine(sender, theirSun) }}</div>
              <p class="gr-dim gr-center">Reading the two skies…</p>
            </template>
          </template>
        </template>

        <!-- 6 · You & the sender -->
        <template v-else-if="step === 6">
          <div class="gr-os" data-testid="gr-our-star">
            <div class="gr-pair">{{ pairLine }}</div>
            <h3>You &amp; {{ Sender }}</h3>
            <template v-if="reading">
              <GiftStar :arms="reading.arms || []" :size="starSize" />
              <p v-if="reading.lead" class="gr-lead">{{ reading.lead }}</p>
              <p class="gr-para" data-testid="gr-star-words">{{ reading.report }}</p>
              <div v-if="reading.mystic" class="gr-mys">{{ reading.mystic.name }}</div>
            </template>
            <p v-else-if="waiting" class="gr-para">I've asked {{ sender }} for their birthday. When it's in, you'll both get your star.</p>
            <p v-else-if="starsError" class="gr-para">The stars are clouded right now. Try again in a moment.</p>
            <p v-else class="gr-dim">Reading the two skies…</p>
          </div>
          <div class="gr-center gr-mt">
            <button type="button" class="gr-go" data-testid="gr-continue" @click="goTo(7)">Continue to your own question ›</button>
          </div>
        </template>

        <!-- 7 · Your own question -->
        <template v-else-if="step === 7">
          <div class="gr-say">
            <div class="gr-who"><img v-if="mystic.avatar && !mysticAvFailed" :src="mystic.avatar" alt="" @error="mysticAvFailed = true" /><span
              v-else-if="mystic.avatar" class="gr-av-ph" data-testid="gr-av-ph" aria-hidden="true">{{ initialOf(mystic.name) }}</span>{{ mystic.name }}</div>
            {{ OWN_HERE }}
          </div>
          <div class="gr-own">
            <div class="gr-seg" role="radiogroup" aria-label="Who it is for" data-testid="gr-who">
              <button type="button" role="radio" :aria-checked="String(!forSomeone)" :class="{ on: !forSomeone }"
                      data-testid="gr-for-me" @click="forSomeone = false">For me</button>
              <button type="button" role="radio" :aria-checked="String(forSomeone)" :class="{ on: forSomeone }"
                      data-testid="gr-for-someone" @click="forSomeone = true">♡ For someone</button>
            </div>
            <div class="gr-mt">
              <button type="button" class="gr-go gr-wide" data-testid="gr-own" @click="emit('own', { forSomeone })">{{ own.icon }} {{ own.label }}<small v-if="priceLabel">{{ priceLabel }}</small></button>
            </div>
            <div v-if="stars" class="gr-mt">
              <button type="button" class="gr-link" data-testid="gr-mirror" @click="emit('mirror')">You &amp; {{ sender }} on my Mirror ›</button>
            </div>
            <!-- Not yet thanked (funnel gap 6, 2026-10-30): "Ask my own
                 question" at step 3 skipped it; the way back, quietly. -->
            <div v-if="!replied" class="gr-mt">
              <button type="button" class="gr-link" data-testid="gr-thank-later" @click="goTo(4)">Tell {{ sender }} what it meant</button>
            </div>
            <KeepThis v-if="keepOffer" />
          </div>
        </template>
      </main>
    </div>
  </section>
</template>

<style scoped>
.grs {
  --gold: #c9a94e; --gold-lt: #f0d9a3; --gold-dk: #8a6a2a; --ink: #2a1845; --ink2: #4a3a63; --muted: #6b5a82;
  --paper: #fbf6ec; --paper-edge: #e4d6b8; --lav: #b9a6d6; --dim: #9b86c9;
  --display: 'Letter Display', 'Cinzel', Georgia, serif; --serif: 'Letter Serif', 'Cormorant Garamond', Georgia, serif;
  --say: Georgia, 'Times New Roman', serif; --ui: Inter, system-ui, -apple-system, 'Segoe UI', sans-serif;
  /* The Menu bar (StagedBar, 65px over the safe area) never covers the lowest button (gift room walk #7). */
  --gr-bar-clear: calc(65px + env(safe-area-inset-bottom, 0px) + 40px);
  position: absolute; inset: 0; z-index: 40; overflow-y: auto; overflow-x: hidden; -webkit-overflow-scrolling: touch;
  scroll-padding-bottom: var(--gr-bar-clear);
  color: #efe6ff; font-family: var(--ui);
  background: radial-gradient(90% 60% at 50% 0%, #241046 0%, #120726 55%, #0b0418 100%);
}
.grs button { font: inherit; cursor: pointer; }
.grs img { display: block; }

/* the frame: the dots and the ✕ */
.gr-top { position: sticky; top: 0; z-index: 20; display: flex; align-items: center; justify-content: center; height: 52px;
  background: linear-gradient(180deg, rgba(18,7,38,.92), rgba(18,7,38,.6) 70%, transparent); }
.gr-dots { display: flex; gap: 8px; }
.gr-dots i { width: 7px; height: 7px; border-radius: 50%; background: rgba(185,166,214,.3); transition: background .4s, transform .4s; }
.gr-dots i.done { background: rgba(240,217,163,.6); }
.gr-dots i.on { background: var(--gold-lt); transform: scale(1.35); box-shadow: 0 0 10px rgba(240,217,163,.7); }
.gr-x { position: absolute; z-index: 25; right: 14px; top: 9px; width: 34px; height: 34px; border-radius: 50%; display: grid;
  place-items: center; background: rgba(20,12,40,.55); border: 1px solid rgba(255,255,255,.14); color: rgba(232,216,255,.85);
  font-size: 15px; padding: 0; }
/* A 44px hit area round the 34px ✕ (e2e phone walk 2026-10-30). */
.gr-x::after { content: ''; position: absolute; inset: -5px; border-radius: 50%; }
.gr-x:focus-visible { outline: 2px solid var(--gold-lt); outline-offset: 2px; }

/* the page: the gift beside the step */
.gr-page { max-width: 1120px; margin: 0 auto; padding: 4px 24px max(110px, var(--gr-bar-clear)); display: grid;
  grid-template-columns: 380px minmax(0, 1fr); gap: 40px; align-items: start; }
.gr-gift { position: sticky; top: 64px; }
.gr-stage { min-height: 60vh; padding-top: 6px; animation: gr-in 520ms cubic-bezier(.2,.7,.2,1) both; }
@keyframes gr-in { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }

/* the gift, on paper */
.gr-paper { position: relative; border-radius: 20px; background: var(--paper); color: var(--ink); border: 1px solid var(--paper-edge);
  overflow: hidden; box-shadow: 0 30px 60px -30px #000; }
.gr-pic { height: 150px; } .gr-pic img { width: 100%; height: 100%; object-fit: cover; }
.gr-note { position: relative; margin: -40px 16px 0; padding: 22px 18px 14px; border-radius: 14px; background: #fffdf8;
  border: 1px solid var(--paper-edge); box-shadow: 0 12px 24px -16px rgba(42,24,69,.5); }
.gr-paper > .gr-note:first-child { margin-top: 24px; }
.gr-seal { position: absolute; top: -15px; left: 50%; transform: translateX(-50%); width: 30px; height: 30px; border-radius: 50%;
  display: grid; place-items: center; font-size: 12px; color: #5a3f0f;
  background: radial-gradient(circle at 35% 30%, #f3d98f, #c9a94e 45%, #8a6a2a); box-shadow: 0 3px 8px rgba(42,24,69,.4); }
.gr-ey { font: 600 10.5px var(--display); letter-spacing: .2em; text-transform: uppercase; color: var(--gold-dk); }
.gr-note .gr-ey { text-align: center; margin-bottom: 8px; }
.gr-note p { margin: 0; font: italic 500 18px/1.42 var(--serif); }
.gr-title { margin: 16px 16px 2px; text-align: center; font: 600 19px var(--display); }
/* The picture's line, between the note and the title. */
.gr-cap { margin: 14px 22px 0; text-align: center; text-wrap: balance; font: italic 500 15.5px/1.4 var(--serif); color: #5a4a72;
  animation: gr-cap-in 520ms cubic-bezier(.2,.7,.2,1) 280ms both; }
@keyframes gr-cap-in { from { opacity: 0; transform: translateY(6px); } }
.gr-cap + .gr-title { margin-top: 10px; }
@media (prefers-reduced-motion: reduce) { .gr-cap { animation: none; } }
.gr-by { display: flex; align-items: center; justify-content: center; gap: 7px; font: 500 12.5px var(--ui); color: var(--muted); }
.gr-by img { width: 20px; height: 20px; border-radius: 50%; } .gr-by b { color: var(--ink2); }
.gr-from { display: none; text-align: center; padding: 8px 0 0; font: 600 10px var(--display); letter-spacing: .2em;
  color: var(--gold-dk); text-transform: uppercase; }
.gr-row { display: flex; justify-content: center; gap: 6px; padding: 14px 10px 16px; }
/* Each card is its own column: the label wraps inside it and never runs into
   the next (e2e 2026-10-30: "THE SURFACE THE TRUTH BENEATH" printed over). */
.gr-mc { --mc: 52px; flex: 1 1 0; min-width: var(--mc); max-width: 76px; text-align: center; }
.gr-mc .f { width: var(--mc); margin: 0 auto; aspect-ratio: 2/3; border-radius: 6px; overflow: hidden; border: 1.5px solid #d8c69a; background: #efe5d2; }
.gr-mc .f img { width: 100%; height: 100%; object-fit: cover; }
.gr-mc span { display: block; max-width: 100%; margin-top: 5px; font: 600 8px/1.25 var(--display); letter-spacing: .02em; color: var(--muted);
  text-transform: uppercase; white-space: normal; overflow-wrap: anywhere; hyphens: auto; }
.gr-mc.yours .f { border: 1.5px dashed #c9a94e; display: grid; place-items: center; color: #c9a94e; background: #fbf3e0; }
.gr-mc.yours.filled .f { border: 2px solid #c9a94e; box-shadow: 0 0 0 3px rgba(201,169,78,.2); }
.gr-mc.yours span { color: var(--gold-dk); }
/* Level with the cards' art (2:3), whatever the labels' lines below. */
.gr-plus { font-size: 13px; color: #c9a94e; align-self: flex-start; margin: 30px -2px 0; }
img.rev { transform: rotate(180deg); }

/* the Mystic speaks (the built line: gold rule, serif) */
.gr-say { position: relative; margin: 0 0 18px; padding: 1px 0 1px 18px; font: 400 19px/1.6 var(--say); color: #efe6ff; }
.gr-say::before { content: ''; position: absolute; left: 0; top: 4px; bottom: 4px; width: 2px; border-radius: 2px;
  background: linear-gradient(180deg, #f0d9a3, rgba(240,217,163,.4) 38%, rgba(240,217,163,.05)); }
.gr-who { display: flex; align-items: center; gap: 8px; margin-bottom: 4px; font: 600 11px var(--ui); letter-spacing: .14em;
  text-transform: uppercase; color: var(--dim); }
.gr-who img { width: 22px; height: 22px; border-radius: 50%; object-fit: cover; }
.gr-av-ph { display: inline-grid; place-items: center; flex: none; width: 22px; height: 22px; border-radius: 50%;
  background: rgba(240,217,163,.12); font: 600 11px/1 var(--display); letter-spacing: 0; color: var(--gold-lt); }
.gr-by .gr-av-ph { width: 20px; height: 20px; font-size: 10px; background: rgba(201,169,78,.18); color: var(--gold-dk); }
.gr-note-line { font-size: 16px; color: #d8c9f2; }
.gr-note-link { display: inline; padding: 0; border: 0; background: none; cursor: pointer; font: inherit; color: var(--gold-lt);
  text-decoration: underline; text-underline-offset: 3px; text-decoration-color: rgba(240,217,163,.5); }
.gr-note-link:hover { text-decoration-color: currentColor; }
.gr-note-link:focus-visible { outline: 2px solid var(--gold-lt); outline-offset: 2px; border-radius: 3px; }

/* one primary per step: the room's gold */
.gr-go { display: inline-flex; align-items: center; justify-content: center; gap: 9px; min-height: 50px; padding: 12px 26px; border: 0;
  border-radius: 999px; font: 800 15px var(--display) !important; letter-spacing: .04em; color: #2a1a05;
  background: linear-gradient(180deg, #f5d98a, #d9a93f); box-shadow: 0 10px 24px -10px rgba(217,169,63,.85); transition: transform .15s; }
.gr-go:hover { transform: translateY(-1px); }
.gr-go:focus-visible, .gr-quiet:focus-visible, .gr-link:focus-visible, .gr-seg button:focus-visible { outline: 2px solid var(--gold-lt); outline-offset: 3px; }
.gr-go small { font: 700 11.5px var(--display); padding: 2px 10px; border-radius: 999px; color: #f5e6bd; background: rgba(42,26,5,.55); letter-spacing: .08em; }
.gr-quiet { display: inline-flex; align-items: center; justify-content: center; gap: 7px; min-height: 48px; padding: 9px 18px;
  border-radius: 999px; background: transparent; border: 1px solid rgba(233,220,255,.22); color: #b9abd3; font: 500 14px var(--ui) !important; }
.gr-acts { display: flex; flex-wrap: wrap; gap: 12px; align-items: center; }
.gr-link { background: none; border: 0; padding: 0 4px; color: #d8c9f2; font: 600 14px var(--ui) !important; text-decoration: underline;
  text-underline-offset: 4px; text-decoration-color: rgba(216,201,242,.35);
  /* A 44px touch target (e2e round 2); the words look as they did. */
  display: inline-flex; align-items: center; justify-content: center; min-height: 44px; min-width: 44px; }
.gr-center { text-align: center; } .gr-mt { margin-top: 18px; }
.gr-dim { font: italic 500 15px var(--serif); color: var(--dim); }

/* 1 · the letter */
.gr-letter { display: grid; gap: 12px; }
.gr-lc { display: grid; grid-template-columns: 70px 1fr; gap: 14px; padding: 14px; border-radius: 16px; background: rgba(255,255,255,.03);
  border: 1px solid rgba(255,255,255,.08); }
.gr-lc .f { aspect-ratio: 2/3; border-radius: 8px; overflow: hidden; border: 1.5px solid rgba(231,201,131,.55); }
.gr-lc .f img { width: 100%; height: 100%; object-fit: cover; }
.gr-lc .k { font: 600 10.5px var(--display); letter-spacing: .18em; color: #e7c983; text-transform: uppercase; }
.gr-lc .k i { font: italic 500 13.5px var(--serif); letter-spacing: 0; color: var(--lav); text-transform: none; margin-left: 4px; }
.gr-lc b { display: block; margin: 3px 0 4px; font: 600 19px/1.25 var(--serif); color: #fff4dc; }
.gr-lc p { margin: 0; font: 400 15.5px/1.5 var(--say); color: #cdbfe6; }

/* 2 · the deck's place */
.gr-draw { min-height: 360px; position: relative; }

/* 3 · your card */
.gr-yc { display: grid; grid-template-columns: 150px 1fr; gap: 20px; padding: 18px; border-radius: 18px; background: var(--paper);
  color: var(--ink); border: 1px solid var(--paper-edge); box-shadow: 0 30px 60px -30px #000; }
.gr-yc .f { aspect-ratio: 2/3; border-radius: 10px; overflow: hidden; border: 2px solid #c9a94e; box-shadow: 0 10px 20px -10px rgba(42,24,69,.7); align-self: start; }
.gr-yc .f img { width: 100%; height: 100%; object-fit: cover; }
.gr-yc h3 { margin: 4px 0 6px; font: 600 24px/1.2 var(--serif); }
.gr-yc p { margin: 0; font: 400 16.5px/1.55 var(--say); color: var(--ink2); white-space: pre-line; }
.gr-yc .q { font: italic 500 16px var(--serif); color: var(--muted); margin-bottom: 10px; }

/* 4 · thanks */
.gr-receipt { margin-left: auto; }

/* 5 · the birthday */
/* The date's own button is this step's one gold action. */
.gr-bd :deep(.bm-save) { border: 0; border-radius: 999px; color: #2a1a05; font: 800 15px var(--display);
  letter-spacing: .04em; background: linear-gradient(180deg, #f5d98a, #d9a93f); box-shadow: 0 10px 24px -10px rgba(217,169,63,.85); }
.gr-bd { max-width: 520px; margin: 0 auto; padding: 22px; border-radius: 18px; border: 1px solid rgba(240,217,163,.25); background: rgba(20,7,31,.7); }
.gr-sign { text-align: center; padding: 8px 0 2px; }
.gr-glyph { font-family: 'Apple Symbols', 'Segoe UI Symbol', 'Noto Sans Symbols 2', serif; font-variant-emoji: text; width: 112px; height: 112px;
  margin: 0 auto; border-radius: 50%; display: grid; place-items: center; font-size: 54px; color: var(--gold-lt);
  background: radial-gradient(circle, rgba(240,217,163,.18), rgba(123,75,176,.12) 60%, transparent 70%);
  border: 1px solid rgba(240,217,163,.35); box-shadow: 0 0 40px rgba(240,217,163,.15) inset; }
.gr-sign h3 { margin: 12px 0 4px; font: 600 28px var(--display); color: #fff1cf; }
.gr-sign p { margin: 0 auto; max-width: 420px; font: italic 500 20px/1.4 var(--serif); color: #e9dcff; }

/* 6 · the star of the two of you */
.gr-os { position: relative; border-radius: 22px; padding: 22px 22px 18px; overflow: hidden; color: #e9dcff; text-align: center;
  background: radial-gradient(110% 80% at 50% 40%, rgba(123,75,176,.42), rgba(20,7,31,.96) 70%); border: 1px solid rgba(240,217,163,.38);
  box-shadow: 0 24px 60px -26px rgba(240,217,163,.35); }
.gr-pair { font: 600 12px var(--display); letter-spacing: .22em; color: #e7c983; text-transform: uppercase; font-variant-emoji: text; }
.gr-os h3 { margin: 4px 0 2px; font: 600 30px var(--display); color: #fff1cf; }
.gr-para { margin: 0 auto; max-width: 520px; font: italic 500 21px/1.45 var(--serif); color: #fff6e6; }
.gr-lead { margin: 0 auto 6px; max-width: 520px; font: 500 14.5px var(--ui); color: #d8c9f2; }
.gr-mys { margin-top: 12px; font: 600 12px var(--ui); color: var(--dim); }

/* 7 · your own question */
.gr-own { text-align: center; padding: 10px 0; }
.gr-wide { min-width: 300px; }
.gr-seg { display: inline-flex; padding: 3px; border-radius: 999px; border: 1px solid rgba(240,217,163,.28); background: rgba(10,4,24,.35); }
.gr-seg button { min-height: 38px; padding: 0 18px; border: 0; border-radius: 999px; background: none; color: #bfaee0; font: 600 13.5px var(--ui) !important; }
.gr-seg button.on { background: #f0d9a3; color: #2a1a05; }
/* A 44px touch target for For me / For someone (e2e round 2, 2026-10-30). */
@media (pointer: coarse) { .gr-seg button { min-height: 44px; } }

/* the phone */
@media (max-width: 899px) {
  .gr-page { display: block; padding: 0 14px var(--gr-bar-clear); }
  /* The dots' band is solid on a phone: the strip ("From Sam") scrolls under
     it, never shows through it under the dots (e2e walk 2026-10-31, iPhone 13).
     The room's own gradient, sized to the room, so the band matches it. */
  .gr-top { background: radial-gradient(90% 60% at 50% 0%, #241046 0%, #120726 55%, #0b0418 100%) top / 100% 100vh no-repeat #160a30; }
  /* Step 2: the picker carries its own row of the sender's cards, so the
     gift's strip steps aside: once, not twice (gift room walk #9). */
  .grs-step-2 .gr-gift { display: none; }
  .gr-gift { position: static; margin-bottom: 18px; }
  .gr-gift.strip .gr-pic, .gr-gift.strip .gr-note, .gr-gift.strip .gr-cap, .gr-gift.strip .gr-title, .gr-gift.strip .gr-by { display: none; }
  .gr-gift.strip .gr-paper { border-radius: 16px; }
  .gr-gift.strip .gr-row { padding: 10px 8px; gap: 6px; }
  .gr-gift.strip .gr-mc { --mc: 46px; }
  .gr-gift.strip .gr-plus { margin-top: 26px; }
  .gr-gift.strip .gr-from { display: block; }
  .gr-say { font-size: 17.5px; }
  .gr-lc { grid-template-columns: 58px 1fr; padding: 12px; } .gr-lc b { font-size: 18px; } .gr-lc p { font-size: 15px; }
  .gr-yc { grid-template-columns: 96px 1fr; gap: 14px; padding: 14px; } .gr-yc h3 { font-size: 21px; } .gr-yc p { font-size: 15.5px; }
  .gr-acts { flex-direction: column; align-items: stretch; } .gr-acts button { width: 100%; }
  .gr-os { padding: 18px 12px 16px; } .gr-os h3 { font-size: 26px; } .gr-para { font-size: 19px; }
  .gr-wide { min-width: 0; width: 100%; }
}
@media (prefers-reduced-motion: reduce) {
  .gr-stage { animation: none; }
  .gr-dots i { transition: none; }
}
</style>
